/// <reference types="@angular/localize" />

// Block ordinary logs before application modules load; runtime configuration may explicitly enable them later.
import './app/utils/console-policy';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import { ToastrModule } from 'ngx-toastr';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import * as PlotlyJS from 'plotly.js-dist-min';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { HttpLoaderFactory } from './app/app.module';
import { GeneralInterceptor } from './app/common/interceptors/generalInterceptor';
import { AuthService } from './app/modules/auth/_services/auth.service';
import { PlotlyModule } from 'angular-plotly.js';
import { FrontendLoggingConfigService } from './app/common/services/frontend-logging-config.service';

/** Initialize session-protected logging configuration without changing the existing authentication initializer. */
export function frontendLoggingInitializer(config: FrontendLoggingConfigService): () => Promise<void> {
  return (): Promise<void> => config.initialize();
}

export function playerFactory() {
  return player;
}

function appInitializer(authService: AuthService) {
  return () => {
    return new Promise<void>(resolve => {
      authService.getUserByToken().subscribe().add(resolve)
    })
  }
}
const socketIoConfig: SocketIoConfig = {
  url: environment.socketUrl,
  options: {
    transports: ['websocket', 'polling'],
    // Add any additional options here
  },
}


PlotlyModule.plotlyjs = PlotlyJS;


bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
          FormsModule,
          ReactiveFormsModule,
          ToastrModule.forRoot(),
          TranslateModule.forRoot({
            loader: {
              provide: TranslateLoader,
              useFactory: HttpLoaderFactory,
              deps: [HttpClient],
            },
          }),
          AppRoutingModule,
          NgbModule,
          InlineSVGModule.forRoot(),
          SocketIoModule.forRoot(socketIoConfig),
          PlotlyModule
        ),
        DatePipe,
        // Keep the initial closed state until configuration is validated; auth startup remains independent.
        {
            provide: APP_INITIALIZER,
            useFactory: frontendLoggingInitializer,
            multi: true,
            deps: [FrontendLoggingConfigService],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializer,
            multi: true,
            deps: [AuthService],
        },
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import('highlight.js/lib/core'),
                languages: {
                    xml: () => import('highlight.js/lib/languages/xml'),
                    typescript: () => import('highlight.js/lib/languages/typescript'),
                    scss: () => import('highlight.js/lib/languages/scss'),
                    json: () => import('highlight.js/lib/languages/json'),
                },
            },
        },
        { provide: HTTP_INTERCEPTORS, useClass: GeneralInterceptor, multi: true },
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()),
        provideLottieOptions({ player: playerFactory }),
    ]
})
  .catch(err => console.error(err))

// if (environment.production) {
//   enableProdMode()
//   if (window) {
//     window.console.log = function () {}
//   }
// }
